import { currentSprint, positionBetween, TASK_STATUSES, toIsoDate, type TaskStatus } from '@hive/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router';
import { SprintsContext } from '@/cycles/sprints-context';
import { useSprints } from '@/data/cycles';
import { blockedTaskIds, useDependencies } from '@/data/dependencies';
import { useGames } from '@/data/games';
import { useSavedViews, type SavedView } from '@/data/views';
import { queryKeys } from '@/data/keys';
import { labelsFor, useCreateLabel, useLabels } from '@/data/labels';
import { useMembers } from '@/data/members';
import { useCreateTask, useGameTasks, useUpdateTask, type GridTask } from '@/data/tasks';
import { useRealtimeInvalidation } from '@/data/useRealtimeInvalidation';
import { BlockedContext } from './blocked-context';
import { GanttView } from './GanttView';
import { GridToolbar, type GridView } from './GridToolbar';
import { lastGameSlug, rememberGameSlug } from './lastGame';
import {
  groupByStatus,
  NO_FILTERS,
  subtasksByParent,
  type GridFilters,
  type QuickAddPlan,
  type SortKey,
} from './model';
import { QuickAdd } from './QuickAdd';
import { SavedViews } from './SavedViews';
import { TaskBoard } from './TaskBoard';
import { TaskDrawer } from './TaskDrawer';
import { TaskList } from './TaskList';
import { useGridShortcuts } from './useGridShortcuts';
import { configForGame, parseViewConfig, sameConfig, type GridViewConfig } from './views';

/** /grid: back to the game Grid showed last. */
export function GridIndex() {
  return <Navigate to={`/grid/${lastGameSlug()}`} replace />;
}

/** /grid/:slug, with ?view=board and ?task=<id> for the drawer. */
export function GridPage() {
  const { slug = 'studio' } = useParams();
  const [params, setParams] = useSearchParams();
  const games = useGames();
  const members = useMembers();
  const allLabels = useLabels();
  const game = games.data?.find((g) => g.slug === slug);
  const tasks = useGameTasks(game?.id);
  const sprints = useSprints(game?.id);
  const dependencies = useDependencies();
  const createTask = useCreateTask();
  const createLabel = useCreateLabel();
  const updateTask = useUpdateTask();
  const [filters, setFilters] = useState<GridFilters>(NO_FILTERS);
  const [sort, setSort] = useState<SortKey>('manual');
  const savedViews = useSavedViews('grid');
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  // Opening Grid without a view in the URL applies the member's default view, once.
  const defaultPending = useRef(!params.has('view'));
  const quickAddRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Live: the other founder's (and Claude's) changes arrive without a refresh.
  useRealtimeInvalidation('tasks', queryKeys.tasks);
  useRealtimeInvalidation('task_labels', queryKeys.tasks);
  useRealtimeInvalidation('labels', queryKeys.labels);
  useRealtimeInvalidation('task_comments', queryKeys.comments);
  useRealtimeInvalidation('tasks', queryKeys.activity);
  useRealtimeInvalidation('task_comments', queryKeys.activity);
  useRealtimeInvalidation('sprints', queryKeys.sprints);
  useRealtimeInvalidation('task_dependencies', queryKeys.dependencies);

  useEffect(() => {
    if (game) rememberGameSlug(game.slug);
  }, [game]);

  const viewParam = params.get('view');
  const view: GridView = viewParam === 'board' || viewParam === 'gantt' ? viewParam : 'list';
  const openTaskId = params.get('task');
  const today = toIsoDate(new Date());
  const labels = useMemo(() => (game ? labelsFor(allLabels.data ?? [], game.id) : []), [allLabels.data, game]);
  const taskList = useMemo(() => tasks.data ?? [], [tasks.data]);
  const sprintList = useMemo(() => sprints.data ?? [], [sprints.data]);
  // "Current sprint" means whichever sprint that is today; no open sprint matches nothing.
  const effectiveFilters = useMemo(
    () =>
      filters.sprint === 'current' ? { ...filters, sprint: currentSprint(sprintList, today)?.id ?? 'none' } : filters,
    [filters, sprintList, today],
  );
  const columns = useMemo(
    () => groupByStatus(taskList, effectiveFilters, sort, today),
    [taskList, effectiveFilters, sort, today],
  );
  const subtasks = useMemo(() => subtasksByParent(taskList), [taskList]);
  const blocked = useMemo(() => blockedTaskIds(dependencies.data ?? [], taskList), [dependencies.data, taskList]);
  const openTask = openTaskId ? taskList.find((t) => t.id === openTaskId) : undefined;

  const forGame = useCallback(
    (view: SavedView) =>
      configForGame(
        parseViewConfig(view.config),
        new Set(labels.map((l) => l.id)),
        new Set(sprintList.map((sp) => sp.id)),
      ),
    [labels, sprintList],
  );
  const current: GridViewConfig = { view, filters, sort };
  const activeView = savedViews.data?.find((v) => v.id === activeViewId);
  const viewModified = activeView ? !sameConfig(forGame(activeView), current) : false;

  const setParam = useCallback(
    (key: string, value: string | null) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (value === null) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: key === 'view' },
      ),
    [setParams],
  );
  const applyView = useCallback(
    (saved: SavedView) => {
      const config = forGame(saved);
      setFilters(config.filters);
      setSort(config.sort);
      setParam('view', config.view === 'list' ? null : config.view);
      setActiveViewId(saved.id);
    },
    [forGame, setParam],
  );

  useEffect(() => {
    if (!defaultPending.current || !savedViews.data || !game || sprints.isPending || allLabels.isPending) return;
    defaultPending.current = false;
    const fallback = savedViews.data.find((v) => v.is_default);
    // Applied after this render: it changes the URL as well as state.
    if (fallback) queueMicrotask(() => applyView(fallback));
  }, [savedViews.data, game, sprints.isPending, allLabels.isPending, applyView]);

  const openTaskPanel = useCallback((id: string) => setParam('task', id), [setParam]);
  const closeTaskPanel = useCallback(() => setParam('task', null), [setParam]);

  useGridShortcuts({
    onNew: useCallback(() => quickAddRef.current?.focus(), []),
    onSearch: useCallback(() => searchRef.current?.focus(), []),
    onEscape: useCallback(() => {
      if (openTaskId) closeTaskPanel();
    }, [openTaskId, closeTaskPanel]),
  });

  if (games.isPending) return <Status text="Loading games" />;
  if (games.isError) return <Status text={`Can't load games: ${games.error.message}`} />;
  if (!game) {
    return (
      <div className="px-7 pt-14">
        <p className="hud-label mb-3">Grid</p>
        <h1 className="font-display text-2xl font-black text-red uppercase">No game called “{slug}”</h1>
        <p className="mt-2 text-muted">
          <Link className="text-cyan underline" to="/grid/studio">
            Open Studio
          </Link>{' '}
          or{' '}
          <Link className="text-cyan underline" to="/games">
            manage games
          </Link>
          .
        </p>
      </div>
    );
  }

  async function addFromQuickAdd(plan: QuickAddPlan) {
    const created = await Promise.all(
      plan.newLabels.map((name) => createLabel.mutateAsync({ name, game_id: game!.id })),
    );
    const last = taskList.reduce<string | null>(
      (max, t) => (max === null || t.position > max ? t.position : max),
      null,
    );
    await createTask.mutateAsync({
      game_id: game!.id,
      title: plan.title,
      priority: plan.priority ?? 4,
      assignee_id: plan.assignee?.id ?? null,
      due_date: plan.due,
      status: 'todo',
      position: positionBetween(last, null),
      label_ids: [...plan.labels.map((l) => l.id), ...created.map((l) => l.id)],
    });
  }

  const move = (task: GridTask, change: { status?: TaskStatus; position?: string }) =>
    updateTask.mutate({ id: task.id, gameId: task.game_id, patch: change });

  return (
    <SprintsContext value={sprintList}>
      <BlockedContext value={blocked}>
        <div className="flex h-full min-h-0 flex-col gap-4 px-6 pt-6 pb-2">
          <header className="flex flex-wrap items-baseline gap-3">
            <p className="hud-label">Grid</p>
            <h1 className="font-display text-2xl font-black tracking-[0.04em] text-cyan uppercase text-shadow-glow-cyan">
              {game.name}
            </h1>
            {game.archived_at ? <span className="font-mono text-xs text-yellow">archived</span> : null}
            <span className="font-mono text-xs text-muted">
              {taskList.filter((t) => t.status !== 'done').length} open
            </span>
            <div className="ml-auto self-center">
              <SavedViews
                views={savedViews.data ?? []}
                active={activeView}
                modified={viewModified}
                current={current}
                onApply={applyView}
                onActivate={(v) => setActiveViewId(v.id)}
              />
            </div>
          </header>
          <QuickAdd ref={quickAddRef} members={members.data ?? []} labels={labels} onAdd={addFromQuickAdd} />
          <GridToolbar
            ref={searchRef}
            view={view}
            onView={(v) => setParam('view', v === 'list' ? null : v)}
            filters={filters}
            onFilters={setFilters}
            sort={sort}
            onSort={setSort}
            members={members.data ?? []}
            labels={labels}
            sprints={sprintList}
          />
          {tasks.isError ? <p className="text-red">Can't load tasks: {tasks.error.message}</p> : null}
          {tasks.isPending ? (
            <Status text="Loading tasks" />
          ) : view === 'gantt' ? (
            <GanttView
              tasks={TASK_STATUSES.flatMap((st) => columns[st.id])}
              dependencies={dependencies.data ?? []}
              sprints={sprintList}
              today={today}
              onOpen={openTaskPanel}
              onReschedule={(task, dates) => updateTask.mutate({ id: task.id, gameId: task.game_id, patch: dates })}
            />
          ) : view === 'board' ? (
            <TaskBoard
              columns={columns}
              sort={sort}
              labels={labels}
              members={members.data ?? []}
              subtasks={subtasks}
              today={today}
              onOpen={openTaskPanel}
              onMove={move}
            />
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto pb-6">
              <TaskList
                columns={columns}
                labels={labels}
                members={members.data ?? []}
                subtasks={subtasks}
                today={today}
                onOpen={openTaskPanel}
                onToggleDone={(t) => move(t, { status: t.status === 'done' ? 'todo' : 'done' })}
              />
            </div>
          )}
          {openTask ? (
            <TaskDrawer
              task={openTask}
              subtasks={subtasks.get(openTask.id) ?? []}
              game={game}
              members={members.data ?? []}
              labels={labels}
              onClose={closeTaskPanel}
              onOpen={openTaskPanel}
            />
          ) : null}
        </div>
      </BlockedContext>
    </SprintsContext>
  );
}

function Status({ text }: { text: string }) {
  return (
    <p className="hud-label px-7 pt-14" role="status">
      {text}
    </p>
  );
}
